import { Alert, AlertTitle, Dialog, LinearProgress } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCollectionPfpFees, getPFPFees } from '../helpers/Api'
import { getBoostAction } from '../helpers/WaxApi'
import { useSharedState } from '../waxplorer/Waxplorer'
import { DropAlert } from './DropAlert'
import { DropPaper } from './DropPaper'
import MainButton from '../common/util/input/MainButton'
import InputField from '../common/util/InputField'

export const ManagePFPFeeDialog = ({
    open,
    setOpen,
    collectionName,
    feeBalance,
    setFeeBalance,
}) => {
    const [state] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null

    const [fee, setFee] = useState(0.1)
    const [waxAmount, setWaxAmount] = useState(0)
    const [withdrawAmount, setWithdrawAmount] = useState(0)
    const [isLoading, setIsLoading] = useState(0)
    const [transaction, setTransaction] = useState()

    const { t } = useTranslation('common')

    const parseCollectionFees = (res) => {
        if (res && res['quantity']) {
            setFeeBalance(parseFloat(res['quantity'].replace(' WAX', '')))
        }
    }

    const parseFees = (res) => {
        if (res && res['pfpfees']) {
            setFee(parseFloat(res['pfpfees']).toFixed(8))
        }
    }

    useEffect(() => {
        getCollectionPfpFees(collectionName, state).then(parseCollectionFees)
        getPFPFees(state).then(parseFees)
    })

    const withdrawFees = async () => {
        setIsLoading(true)
        const res = await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'nfthivedrops',
                        name: 'withdrawfee',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            authorized_account: userName,
                            collection_name: collectionName,
                            amount: `${parseFloat(withdrawAmount).toFixed(
                                8,
                            )} WAX`,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
        if (res?.response?.transaction_id) {
            setFeeBalance(feeBalance - withdrawAmount)
            setTransaction(res['transactionId'])
        }

        setIsLoading(false)
    }

    const payAmount = async () => {
        setIsLoading(true)
        const res = await activeUser.session.transact(
            {
                actions: [
                    getBoostAction(activeUser),
                    {
                        account: 'eosio.token',
                        name: 'transfer',
                        authorization: [
                            {
                                actor: userName,
                                permission: activeUser.requestPermission
                                    ? activeUser.requestPermission
                                    : 'active',
                            },
                        ],
                        data: {
                            from: userName,
                            to: 'nfthivedrops',
                            quantity: `${parseFloat(waxAmount).toFixed(8)} WAX`,
                            memo: 'pfp_fees:' + collectionName,
                        },
                    },
                ],
            },
            {
                expireSeconds: 300,
                blocksBehind: 0,
            },
        )
        if (res?.response?.transaction_id) {
            setFeeBalance(feeBalance + waxAmount)
            setTransaction(res['transactionId'])
        }

        setIsLoading(false)
    }

    const close = () => {
        setOpen(false)
    }

    return (
        <Dialog open={open} onClose={() => close()} maxWidth="md" fullWidth>
            <DropPaper
                id={'FeeEditor'}
                title={t('drops.pfp_fees')}
                error={waxAmount <= 0}
                noMargin
            >
                <DropAlert
                    condition={true}
                    severity="warning"
                    title="Fee Balance"
                >
                    {t('drops.your_current_balance_is_x', {
                        x: feeBalance,
                    })}{' '}
                    <br />
                    {t('drops.your_new_balance_will_be_x', {
                        x: feeBalance + (waxAmount >= 0 ? waxAmount : 0),
                    })}{' '}
                    <br />
                    This will be enough for{' '}
                    {(feeBalance + (waxAmount >= 0 ? waxAmount : 0)) / fee}{' '}
                    Mints
                </DropAlert>
                <div className={'w-full flex flex-wrap mt-4'}>
                    <div className={'w-40'}>
                        <InputField
                            type="number"
                            variant="bordered"
                            size="sm"
                            isRequired={false}
                            label={t('drops.amount_to_pay')}
                            value={waxAmount}
                            min={0}
                            onWheel={(e) => e.target.blur()}
                            onChange={(e) => {
                                setWaxAmount(parseFloat(e.target.value))
                            }}
                            isDisabled={isLoading}
                            isInvalid={!waxAmount || waxAmount <= 0}
                        />
                    </div>
                    <MainButton
                        className="mx-2 md:mx-5"
                        fullWidth={true}
                        onClick={payAmount}
                        isDisabled={!waxAmount || isLoading}
                    >
                        {t('drops.pay_fees')}
                    </MainButton>
                    <div className={'w-40 md:ml-8'}>
                        <InputField
                            type="number"
                            variant="bordered"
                            size="sm"
                            label={t('drops.amount_to_withdraw')}
                            value={withdrawAmount}
                            min={0}
                            max={feeBalance}
                            onWheel={(e) => e.target.blur()}
                            onChange={(e) => {
                                setWithdrawAmount(parseFloat(e.target.value))
                            }}
                            isDisabled={!feeBalance}
                            isInvalid={
                                !withdrawAmount ||
                                withdrawAmount > feeBalance ||
                                withdrawAmount <= 0
                            }
                        />
                    </div>

                    <MainButton
                        className="mx-3 md:mx-5"
                        fullWidth={true}
                        onClick={withdrawFees}
                        isDisabled={
                            !withdrawAmount ||
                            withdrawAmount > feeBalance ||
                            isLoading
                        }
                    >
                        {t('asset.withdraw')}
                    </MainButton>
                    <div className="md:mx-8 mt-1 w-28">
                        <MainButton
                            fullWidth={true}
                            onClick={() => close()}
                            color="danger"
                        >
                            Cancel
                        </MainButton>
                    </div>
                </div>
                {isLoading ? (
                    <div className={'mt-4'}>
                        <LinearProgress />
                    </div>
                ) : (
                    ''
                )}
                {transaction ? (
                    <div className={'mt-4'}>
                        <Alert
                            sx={{
                                '.MuiAlert-message': {
                                    width: '100%',
                                },
                            }}
                            title={'Transaction'}
                            severity={'success'}
                            onClose={() => setTransaction(null)}
                        >
                            <AlertTitle>Transaction</AlertTitle>
                            <a
                                href={
                                    (process.env.NEXT_PUBLIC_TESTNET === 'TRUE'
                                        ? 'https://testnet.waxblock.io/transaction/'
                                        : 'https://waxblock.io/transaction/') +
                                    transaction
                                }
                                target="_blank"
                            >
                                {transaction}
                            </a>
                        </Alert>
                    </div>
                ) : (
                    ''
                )}
            </DropPaper>
        </Dialog>
    )
}
